import { readableDate } from '@pams/shared';

/**
 * Every word the bell says, in one place — so prose carries a test and cannot be deleted in a
 * tidy-up without something going red. `pages/Dashboard/copy.ts`'s stated reason.
 *
 * ⚠️ A FILE OF ITS OWN, NOT AN EXPORT FROM `NotificationBell.tsx`.
 * `react-refresh/only-export-components` is configured as a warning and `pnpm lint` runs with
 * `--max-warnings 0`, so a component file that also exports a constant fails the build outside
 * `components/ui/**`. `lib/navigation.ts` records the same constraint.
 */
export const NOTIFICATION_COPY = {
  /**
   * ⚠️ THE ACCESSIBLE NAME CARRIES BOTH NUMBERS, because the badge is `aria-hidden` and the
   * NEW dot has no text at all. A bell announcing only "Notifications" would tell a screen
   * reader user nothing the sighted badge tells everybody else.
   */
  trigger: (total: number, newCount: number, unseenNotices = 0): string =>
    (total === 0
      ? 'Notifications: nothing outstanding'
      : `Notifications: ${String(total)} order${total === 1 ? '' : 's'} still to fill` +
        (newCount > 0 ? `, ${String(newCount)} new` : '')) +
    /* ⚠️ [P7B §6b] Unseen notices add WORDS as well as a dot — the dot is `aria-hidden`. */
    (unseenNotices > 0 ? `, ${String(unseenNotices)} new notice${unseenNotices === 1 ? '' : 's'}` : ''),
  /** ⚠️ The SERVER's window, echoed — never re-derived in a zone the client does not own. */
  window: (from: string, to: string): string =>
    `Still to fill · ${readableDate(from) ?? from} to ${readableDate(to) ?? to}`,
  loading: 'Still to fill',
  empty: 'Nothing outstanding in the next few days.',
  /**
   * ⚠️ [C6a] `rest` IS ORDERS WITH FORCE WORK — the server's `forceTotal` less the force entries
   * shown. An order whose only open work is a command role is on the bell and never in Assign PA,
   * so a figure taken from `total` would promise rows the inbox does not have.
   */
  more: (rest: number): string => `${String(rest)} more in Assign PA`,
  /**
   * ⚠️ [P7B §5d] A TYPE, FOLDED — one row for all its orders, never one entry each. 182 Puja orders
   * published in an evening would otherwise take every one of the eight entries.
   */
  typeGroup: (label: string, total: number, newCount: number): string =>
    `${label} — ${String(total)} order${total === 1 ? '' : 's'} still to fill` +
    (newCount > 0 ? ` · ${String(newCount)} new` : ''),
  /** [P7B §6b] The notices section — what was TOLD, above what is LEFT. Never names the super admin. */
  noticesHeading: 'Notices',
  notice: (name: string, title: string): string => `${name} · ${title} was taken back to draft by a super admin.`,
  noticeReason: (reason: string): string => `Reason: ${reason}`,
} as const;
